// =======================================================
// Recorder.js — record the canvas to a .webm file
// =======================================================
//
// Uses canvas.captureStream(fps) + MediaRecorder. This records at the
// canvas' native resolution (i.e. whatever we set via engine.setRenderSize),
// so if your render is 10900×540, your WebM will be 10900×540.
//
// Codec preference order: vp9 > vp8 > default. Bitrate is high by
// default — LED walls forgive little.

export class Recorder {
  constructor(canvas, engine) {
    this.canvas = canvas;
    this.engine = engine;
    this.stream = null;
    this.mediaRecorder = null;
    this.chunks = [];
    this.startTs = 0;
    this.exporting = false;
    this._cancelExport = false;
    this._exportCanceled = false;
    this.onStart = null;
    this.onStop  = null;
    this.onExportStart = null;
    this.onExportStop = null;
    this.onTick  = null;
    this.onError = null;
    this._tickHandle = null;
  }

  async _yieldToBrowser() {
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  isRecording() { return !!this.mediaRecorder && this.mediaRecorder.state === 'recording'; }

  start({ fps = 50, bitrate = 25_000_000 } = {}) {
    if (this.isRecording()) return;

    this.engine.running = true;
    this.engine.restartLoop();

    this.stream = this.canvas.captureStream(fps);
    const mimeCandidates = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ];
    let mime = '';
    for (const m of mimeCandidates) {
      if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
    }
    if (!mime) {
      throw new Error('No supported WebM codec found in this browser.');
    }

    this.chunks = [];
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: mime,
      videoBitsPerSecond: bitrate
    });
    this.mediaRecorder.ondataavailable = e => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `aiday-${ts}.webm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // don't revoke immediately — some browsers need the URL to live a bit
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      this.chunks = [];
      this.mediaRecorder = null;
      if (this.stream) {
        for (const t of this.stream.getTracks()) t.stop();
        this.stream = null;
      }
      if (this._tickHandle) {
        clearInterval(this._tickHandle);
        this._tickHandle = null;
      }
      if (this.onStop) this.onStop();
    };

    this.startTs = performance.now();
    this.mediaRecorder.start(1000);                   // emit chunks every second
    if (this.onStart) this.onStart();
    if (this.onTick) {
      this._tickHandle = setInterval(() => {
        this.onTick((performance.now() - this.startTs) / 1000);
      }, 100);
    }
  }

  stop() {
    if (!this.isRecording()) return;
    this.mediaRecorder.stop();
  }

  toggle() {
    this.isRecording() ? this.stop() : this.start();
  }

  isExporting() { return this.exporting; }
  cancelExport() { this._cancelExport = true; }

  // Snapshot a single PNG frame at the current canvas resolution.
  snapshot() {
    this.canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `aiday-${ts}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }, 'image/png');
  }

  async exportPngSequence({ fps = 50, duration = 10, renderWidth = null, renderHeight = null, warmupDuration = 0 } = {}) {
    if (this.isRecording() || this.exporting) return;
    if (!window.showDirectoryPicker) {
      throw new Error('PNG sequence export requires a Chromium browser with folder access.');
    }

    const totalFrames = Math.max(1, Math.round(fps * duration));
    const dt = 1 / fps;
    const dir = await window.showDirectoryPicker({ mode: 'readwrite' });
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    const folderName = `aiday-png-${ts}-${fps}fps`;
    const folder = await dir.getDirectoryHandle(folderName, { create: true });

    const prevRunning = this.engine.running;
    const prevLastTs = this.engine._lastTs;
    const prevWidth = this.engine.width;
    const prevHeight = this.engine.height;
    const prevTime = this.engine.time;
    const prevMediaTime = this.engine.mediaTime;
    let framesWritten = 0;
    this.exporting = true;
    this._cancelExport = false;
    this._exportCanceled = false;
    this.engine.externalFrameControl = true;
    this.engine.running = true;
    if (renderWidth && renderHeight) {
      this.engine.setRenderSize(renderWidth, renderHeight);
    }
    this.engine.restartLoop();
    if (this.onExportStart) this.onExportStart({ folderName, totalFrames, fps });

    try {
      const warmupFrames = Math.max(0, Math.round(fps * warmupDuration));
      for (let i = 0; i < warmupFrames; i++) {
        if (this._cancelExport) {
          this._exportCanceled = true;
          break;
        }
        this.engine.renderFixedFrame(dt);
        if ((i + 1) % 4 === 0) {
          await this._yieldToBrowser();
        }
      }

      this.engine.time -= dt;
      this.engine.mediaTime = 0;
      this.engine.currentGenerator?.onMediaTimeReset?.(0);
      this.engine.mediaTime = -dt;

      for (let i = 0; i < totalFrames; i++) {
        if (this._cancelExport) {
          this._exportCanceled = true;
          break;
        }

        this.engine.renderFixedFrame(dt);
        const blob = await new Promise((resolve, reject) => {
          this.canvas.toBlob((png) => {
            if (png) resolve(png);
            else reject(new Error(`Failed to encode PNG for frame ${i + 1}.`));
          }, 'image/png');
        });

        const handle = await folder.getFileHandle(`frame-${String(i + 1).padStart(6, '0')}.png`, { create: true });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        framesWritten = i + 1;

        if (this.onTick) this.onTick((i + 1) / fps);
        if ((i + 1) % 2 === 0) {
          await this._yieldToBrowser();
        }
      }
      return { folderName, framesWritten, totalFrames, canceled: this._exportCanceled };
    } catch (error) {
      if (this.onError) this.onError(error);
      throw error;
    } finally {
      this.engine.externalFrameControl = false;
      this.engine.running = prevRunning;
      this.engine._lastTs = prevLastTs;
      this.engine.time = prevTime;
      this.engine.mediaTime = prevMediaTime;
      if (renderWidth && renderHeight) {
        this.engine.setRenderSize(prevWidth, prevHeight);
      }
      this.exporting = false;
      this._cancelExport = false;
      if (this.onExportStop) this.onExportStop({
        canceled: this._exportCanceled,
        folderName,
        framesWritten,
        totalFrames
      });
      this._exportCanceled = false;
    }
  }
}
